import {
  EVONE_APP_CODE, REDIRECT_EXCEPTION_ORG_IDS, INGREDIENTS, TRUE_DESIGN_APP_CODE, BillingType,
} from 'constants/AppConstants';
import { ENABLE_MULTI_PAYMENT_METHODS } from 'constants/FeatureFlag.constants'; // #genai

export const featureFlagSelector = (flagName) => ({ featureFlag }) => featureFlag[flagName];

export const isEntitledForMyEVIngredientSelector = (ingredient) => ({ userEntitlements }) => Boolean(userEntitlements?.myev?.includes(ingredient));
export const isEntitledForPropertyIngredientSelector = (ingredient) => ({ userEntitlements }) => Boolean(userEntitlements?.property?.includes(ingredient));

export const isOrderingDisabledSelector = isEntitledForMyEVIngredientSelector(INGREDIENTS.DISABLE_ORDERING);
// #genai ENABLE_ACH is registered under the `property` app in the ingredient catalog (id 8103),
// even though its description names MYEV. Read it from the `property` entitlements list so that
// admins who provision ACH via the property app surface it in the MYEV UI.
export const isAchEnabledSelector = isEntitledForPropertyIngredientSelector(INGREDIENTS.ENABLE_ACH);
export const isAchEligibleSelector = (state) => {
  const billingInformation = state.userData?.billingInformation;
  if (!billingInformation) return false;
  const isTermsBilling = billingInformation.billingType === BillingType.TERMS;
  const isAchTermsType = billingInformation.isTermsUser
    && ['Net0'].includes(billingInformation.termsType);
  return isTermsBilling && isAchTermsType && isAchEnabledSelector(state);
}; // #genai

export const isEntitledForHidePricingSelector = isEntitledForMyEVIngredientSelector(INGREDIENTS.HIDE_PRICING);

export const isEntitledForImageAvailabiltySelector = (state) => state.userData?.initParameters?.isImageCoverageCheckEnabled;

export const isSuperUserSelector = isEntitledForMyEVIngredientSelector(INGREDIENTS.ALLOW_UPGRADE);

export const isEntitledForAnalyticsSelector = isEntitledForMyEVIngredientSelector(INGREDIENTS.ANALYTICS);

export const isEntitledForGlobalSearchSelector = isEntitledForMyEVIngredientSelector(INGREDIENTS.GLOBAL_SEARCH);

export const isEntitledForSubscriptionDashboardSelector = isEntitledForMyEVIngredientSelector(INGREDIENTS.SUBSCRIPTION_DASHBOARD);

export const isSubscriptionDashboardV2EnabledSelector = isEntitledForMyEVIngredientSelector(INGREDIENTS.SUBSCRIPTION_DASHBOARD_V2); // #genai

// #genai Phase 2 multi-payment-methods gate: feature flag must be ON and user must be ACH-eligible.
export const isMultiplePaymentMethodsEnabledSelector = (state) => (
  Boolean(featureFlagSelector(ENABLE_MULTI_PAYMENT_METHODS)(state)) && isAchEligibleSelector(state)
); // #genai

// #genai True after bill-payer-info succeeds (data present) or fails (error.billingInformation
// leaves its null init). Distinguishes "not loaded yet" from "not eligible".
export const isBillingContextResolvedSelector = (state) => {
  const { billingInformation, error } = state.userData || {};
  const hasBillingInformation = Boolean(billingInformation) && Object.keys(billingInformation).length > 0;
  return hasBillingInformation || (error?.billingInformation ?? null) !== null;
}; // #genai

// #genai Phase 2 — saved payment methods selectors (state.billingInformation.paymentMethods).
export const selectPaymentMethodsState = (state) => state.billingInformation?.paymentMethods || {};
export const selectPaymentMethodList = (state) => {
  const byId = selectPaymentMethodsState(state).byId || {};
  return Object.values(byId).sort(
    (a, b) => Number(Boolean(b.isDefault)) - Number(Boolean(a.isDefault)) || b.id - a.id,
  );
};
export const selectPaymentMethodsLoading = (state) => Boolean(selectPaymentMethodsState(state).loading);
export const selectPaymentMethodsLoaded = (state) => Boolean(selectPaymentMethodsState(state).loaded);
export const selectPaymentMethodsLoadError = (state) => selectPaymentMethodsState(state).loadError || null;
export const selectPaymentMethodsActionError = (state) => selectPaymentMethodsState(state).actionError || null;
export const selectPendingPaymentMethodActionKey = (state) => selectPaymentMethodsState(state).pendingActionKey || null;

// #genai Phase 2 — "Payment method added" success-banner payload, set by Add-Ach / Add-Card
// on save and consumed by SavedPaymentMethods on mount.
export const selectRecentlyAddedPaymentMethod = (state) => state.billingInformation?.recentlyAddedMessage || null;

export const isRedirectExceptionOrgSelector = ({ userData: { initParameters } }) => REDIRECT_EXCEPTION_ORG_IDS.includes(initParameters?.rootOrgNodeId);

export const hasTrueDesignAppEntitlementSelector = ({ userData: { entitledApps } }) => entitledApps?.some((app) => app.appCode === TRUE_DESIGN_APP_CODE);

export const isEntitledForEmbeddedExplorerSelector = isEntitledForPropertyIngredientSelector(INGREDIENTS.EMBEDDED_VIEWER);

export const evoneAppSelector = ({
  userData: {
    entitledThirdPartyApps,
    loading: { entitledThirdPartyApps: loadingEntitledThirdPartyApps },
  },
}) => (
  !loadingEntitledThirdPartyApps && entitledThirdPartyApps?.find((app) => app.appCode === EVONE_APP_CODE));
